import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';
import { Badge, Box, Button, IconButton } from '@mui/material';
import { Permission } from 'interfaces/permissionOption';
import React from 'react';

import { usePermissions } from 'utils/usePermissions';
import { useCopilotDrawerState } from '../hooks/useCopilotDrawerState';

interface Props {
  animateOnEnter?: boolean;
  iconOnly?: boolean;
}

export const CopilotDrawerTrigger: React.FC<React.PropsWithChildren<Props>> = ({
  animateOnEnter = false,
  iconOnly = false,
}) => {
  const { hasPermission } = usePermissions();
  const canViewNucleaiCopilot =
    hasPermission(Permission.AccessAnalysisStudio) && hasPermission(Permission.ViewNucleaiCopilot);
  const { copilotDrawerActivityCount, copilotDrawerOpen, setCopilotDrawerOpen } = useCopilotDrawerState();
  const [isRevealed, setIsRevealed] = React.useState(!animateOnEnter);
  const showActivityBadge = !copilotDrawerOpen && copilotDrawerActivityCount > 0;
  const triggerAriaLabel = showActivityBadge
    ? `Copilot, ${copilotDrawerActivityCount} active update${copilotDrawerActivityCount === 1 ? '' : 's'}`
    : 'Copilot';
  const triggerIcon = (
    <Badge color="primary" badgeContent={copilotDrawerActivityCount} invisible={!showActivityBadge} max={9}>
      <FaceRetouchingNaturalIcon />
    </Badge>
  );

  React.useEffect(() => {
    if (!animateOnEnter) {
      setIsRevealed(true);
      return undefined;
    }

    setIsRevealed(false);
    const animationFrame = window.requestAnimationFrame(() => {
      setIsRevealed(true);
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [animateOnEnter]);

  if (!canViewNucleaiCopilot) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        maxWidth: animateOnEnter ? (isRevealed ? (iconOnly ? 48 : 144) : 0) : 'none',
        opacity: animateOnEnter ? (isRevealed ? 1 : 0) : 1,
        transform: animateOnEnter ? `translateX(${isRevealed ? '0px' : '18px'})` : 'none',
        transition: (theme) =>
          theme.transitions.create(['max-width', 'opacity', 'transform'], {
            duration: theme.transitions.duration.enteringScreen,
            easing: theme.transitions.easing.easeOut,
          }),
      }}
    >
      {iconOnly ? (
        <IconButton
          aria-label={triggerAriaLabel}
          title={triggerAriaLabel}
          color="inherit"
          onClick={() => setCopilotDrawerOpen(true, 'replaceIn')}
          data-test-class="copilot-drawer-trigger"
          sx={{
            borderRadius: 999,
            p: 1,
            backgroundColor: copilotDrawerOpen ? 'action.selected' : showActivityBadge ? 'action.hover' : 'transparent',
          }}
        >
          {triggerIcon}
        </IconButton>
      ) : (
        <Button
          aria-label={triggerAriaLabel}
          color="inherit"
          startIcon={triggerIcon}
          onClick={() => setCopilotDrawerOpen(true, 'replaceIn')}
          data-test-class="copilot-drawer-trigger"
          sx={{
            borderRadius: 999,
            px: 1.5,
            py: 0.75,
            minWidth: 0,
            whiteSpace: 'nowrap',
            backgroundColor: copilotDrawerOpen ? 'action.selected' : showActivityBadge ? 'action.hover' : 'transparent',
          }}
        >
          Copilot
        </Button>
      )}
    </Box>
  );
};
